import { useMemo, useState } from 'react';
import * as React from 'react';

import { useUniqueID } from 'support/react/hooks';

import { AccordionItemContext } from './AccordionItemContext';
import { ACCORDION_BUTTON_ID_PREFIX, ACCORDION_PANEL_ID_PREFIX } from './constants';

export interface AccordionItemProps {
  children?: React.ReactNode;
  /**
   * Whether the item should be expanded when it mounts.
   *
   * Changing its value after the component has mounted will not cause any
   * update.
   */
  defaultIsExpanded?: boolean;
  onToggle?(isOpen: boolean): void;
}

/**
 * An umbrella component that houses related accordion header and panel.
 */
export function AccordionItem({ children, onToggle, defaultIsExpanded = false }: AccordionItemProps) {
  const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);

  const buttonID = useUniqueID(ACCORDION_BUTTON_ID_PREFIX);
  const panelID = useUniqueID(ACCORDION_PANEL_ID_PREFIX);

  const itemContext = useMemo(
    () => ({
      buttonID,
      isExpanded,
      panelID,
      setIsExpanded(value: React.SetStateAction<boolean>) {
        setIsExpanded(value);
        onToggle?.(typeof value === 'boolean' ? value : value(isExpanded));
      },
    }),
    [onToggle, buttonID, isExpanded, panelID],
  );

  return <AccordionItemContext.Provider value={itemContext}>{children}</AccordionItemContext.Provider>;
}
